import React from 'react';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { useLoadState } from '../shell/loadSignal';

// The new UI's look for every EnhancedDataGrid (docs/ui_redesign/11-implementation-plan.md, "Workspace and shared grid").
// Only presentation: the grid keeps its columns, data, filters and handlers. Classic never calls these.

// Status columns get a coloured dot by meaning (SAP Fiori "object status", Oracle Redwood badges).
// The first rule that matches wins, so "Post Failed" is bad before "Posted" is ok.
const TONES = [
	['bad', /fail|error|reject|discrepan|invalid|duplicate|cancel|void/i],
	['neutral', /inactive|not processed|not routed|draft|archiv/i],
	['warn', /missing|not found|hold|unmatched|unmapped|waiting|pending (mapping|map)|exception|partial/i],
	['info', /pending|approval|in progress|routed|level|review|scanning|processing|queued/i],
	['ok', /approved|posted|success|complete|active|paid|matched|mapped|ready/i]
];
export const STATUS_FIELDS = new Set(['status', 'pendingStatus', 'modelStatus', 'invoiceStatus', 'postingStatus', 'invoiceDocumentStatus', 'level2StatusName', 'level3StatusName']);

export function statusTone(value) {
	if (value == null || typeof value === 'object') return null;
	const text = String(value).trim();
	if (!text) return null;
	const hit = TONES.find(([, re]) => re.test(text));
	return hit ? hit[0] : 'neutral';
}

// Adds apw2-st apw2-st-<tone> to status cells, keeping the grid's own getCellClassName.
export function withStatusClass(own) {
	return params => {
		const base = own ? own(params) : '';
		if (!params || !STATUS_FIELDS.has(params.field)) return base || '';
		const tone = statusTone(params.value);
		return tone ? `${base ? base + ' ' : ''}apw2-st apw2-st-${tone}` : (base || '');
	};
}

// Columns may grow into free space but never shrink below their classic width, and are at least wide enough for a
// one-line header ("TOTAL INVOICE AMT" instead of three lines). Status and date columns get room for their usual text.
// Flex columns get a floor too, so wide tables scroll sideways instead of squeezing them. Icon and checkbox columns
// (under 80px) and fixed-size columns keep their size.
const headerMin = c => Math.round(String(c.headerName || '').length * 7.4 + 36);
// At 1366px and below the floors are lower, so fewer columns slide under the pinned Actions column (R8).
export const isNarrow = (w = typeof window !== 'undefined' ? window.innerWidth : 1440) => w <= 1366;
export function relaxColumns(columns, narrow = isNarrow()) {
	if (!Array.isArray(columns)) return columns;
	const F = narrow ? { flex: 100, status: 130, date: 110, time: 160 } : { flex: 120, status: 150, date: 172, time: 172 };
	return columns.map(c => {
		if (!c || c.apw2Sized || c.field === '__check__' || c.resizable === false || (c.width && c.width < 80)) return c; // apw2Sized: a screen set its own new-UI sizes
		if (!c.flex && !c.width) return c;
		let min = Math.max(c.minWidth || 0, narrow ? Math.min(c.width || 0, 140) : (c.width || 0), headerMin(c), c.flex ? F.flex : 0);
		if (STATUS_FIELDS.has(c.field)) min = Math.max(min, F.status);
		if (/time/i.test(c.field)) min = Math.max(min, F.time); // "9/26/2026, 7:24:50 AM" on one line
		else if (/date/i.test(c.field)) min = Math.max(min, F.date);
		if (c.flex) return min > (c.minWidth || 0) ? { ...c, minWidth: min } : c;
		return { ...c, flex: c.width / 100, minWidth: min };
	});
}

// The row actions column (Edit, Delete, and Save/Cancel while a row is edited) stays pinned to the right, so a wide grid
// never hides Save off screen. A grid's own pinning wins.
const isActions = c => c && (c.type === 'actions' || /^actions?$/i.test(c.field || '') || /^actions?$/i.test(c.headerName || ''));
export function pinActions(initialState, columns) {
	const fields = (Array.isArray(columns) ? columns : []).filter(isActions).map(c => c.field);
	if (!fields.length || (initialState && initialState.pinnedColumns)) return initialState;
	return { ...initialState, pinnedColumns: { left: [], right: fields } };
}

// The empty grid says what is happening and what to try, instead of "No rows": still loading, the load failed, no
// match for the search or filters, nothing in the chosen period, or simply nothing yet.
export function emptyMessage({ loading, failed, searching, period, canRetry }) {
	if (loading) return { kind: 'loading', title: 'Loading…' };
	if (failed) return { kind: 'error', title: 'Couldn\'t load this list', hint: canRetry ? 'Check your connection, then try again.' : 'Check your connection, then refresh the page.' };
	if (searching) return { kind: 'empty', title: 'No matches', hint: 'Try a different search, or clear the filters.' };
	if (period) return { kind: 'empty', title: 'Nothing in this period', hint: 'Try a longer period.' };
	return { kind: 'empty', title: 'Nothing here yet' };
}

const refreshOf = root => {
	const icon = root && root.querySelector('svg[data-testid="RefreshIcon"]');
	return icon ? icon.closest('button') : null;
};

export function NoRowsOverlay() {
	const apiRef = useGridApiContext();
	const { loading, failed } = useLoadState();
	const api = apiRef && apiRef.current;
	const root = api && api.rootElementRef ? api.rootElementRef.current : null;
	const fm = (api && api.state && api.state.filter && api.state.filter.filterModel) || {};
	const searching = (fm.quickFilterValues || []).some(v => v != null && String(v).trim() !== '')
		|| (fm.items || []).some(i => i && i.value != null && i.value !== '');
	const period = !!(root && root.querySelector('input[name^="weeks-to-filter"]'));
	const retry = refreshOf(root);
	const m = emptyMessage({ loading, failed, searching, period, canRetry: !!retry });
	if (m.kind === 'loading') {
		return (
			<div className="apw2-grid-empty is-loading" role="status" aria-live="polite">
				<div className="apw2-grid-skel" aria-hidden="true"><i /><i /><i /></div>
				<span className="apw2-sr">{m.title}</span>
			</div>
		);
	}
	return (
		<div className={'apw2-grid-empty' + (m.kind === 'error' ? ' is-error' : '')} role="status">
			{m.kind === 'error' ? <ErrorOutlineRounded aria-hidden="true" /> : <SearchOffRounded aria-hidden="true" />}
			<div>
				<b>{m.title}</b>
				{m.hint ? <span>{m.hint}</span> : null}
				{m.kind === 'error' && retry ? <button type="button" className="apw2-linkbtn" onClick={() => retry.click()}>Try again</button> : null}
			</div>
		</div>
	);
}
